import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { exceptionMessageHandler } from '$utils'
import { buildSupplierSections } from './_common/helpers/helpers'
import { SupplierSections } from './_common/helpers/supplierSection'
import { Dayjs } from 'dayjs'
import { InvoiceDateProperty } from '$constants'
import { stockReceivedService, supplierStatementService } from '$services'

interface ReceivedInvoicesState {
  supplierSections: SupplierSections | null
}

export interface ReceivedInvoicesSlice extends ReceivedInvoicesState {
  resetReceivedInvoicesSlice: () => void

  /**
   * Get stock received items (orders and statements)
   * @param from - start date
   * @param to - end date
   */
  getStockReceivedItemsAndStatements(from: Dayjs, to: Dayjs): Promise<void>
}

const initialState: ReceivedInvoicesState = {
  supplierSections: null,
}

const createReceivedInvoicesSlice: StateCreator<
  ReceivedInvoicesSlice & SharedSlice,
  [],
  [],
  ReceivedInvoicesSlice
> = (set) => ({
  ...initialState,
  resetReceivedInvoicesSlice: () => set(initialState),

  getStockReceivedItemsAndStatements: async (
    from: Dayjs,
    to: Dayjs
  ): Promise<void> => {
    try {
      // We have to subtract 1 day from the end of the range because the LoadedCalendarReportFilter
      // component sets the end date as the company start time of the next day. Since the InvoicedAt
      // property is a DateOnly in the backend, if we don't subtract 1 day from the end of the range,
      // we will end up including an extra day within the range than we actually need.

      // Additionally, because the generated stock client sends dates in UTC, we need to convert them
      // back to the company's local time and timezone on the backend to correctly filter by the
      // intended day
      const stockReceivedItemsPromise =
        stockReceivedService.getStockReceivedItems(
          from,
          to.subtract(1, 'day'),
          InvoiceDateProperty.Invoiced
        )
      const statementsPromise = supplierStatementService.getSupplierStatements(
        from,
        to,
        undefined,
        false
      )

      const [stockReceivedItems, statements] = await Promise.all([
        stockReceivedItemsPromise,
        statementsPromise,
      ])

      const supplierSections: SupplierSections | null = buildSupplierSections(
        stockReceivedItems,
        statements
      )
      set(() => ({
        supplierSections: supplierSections,
      }))
    } catch (error) {
      exceptionMessageHandler(
        error,
        'Failed to retrieve stock received items or statements'
      )
    }
  },
})

export default createReceivedInvoicesSlice
